import { Box, Chip, Stack, Typography } from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import useResizeObserver from "@entrilia/shared/hooks/useResizeObserver";
import { toLabel } from "../../utils/withValue";
import { toFilterValue } from "./filterValuesQuery";

const CHIP_GAP = 4;
const OVERFLOW_LABEL_WIDTH = 40;

const noop = () => undefined;

const getVisibleCount = (chipWidths: number[], availableWidth: number, totalCount: number): number => {
  let used = 0;
  let fitting = 0;
  for (const width of chipWidths) {
    const next = used + (fitting === 0 ? 0 : CHIP_GAP) + width;
    const reserved = fitting + 1 < totalCount ? OVERFLOW_LABEL_WIDTH : 0;
    if (next + reserved > availableWidth) {
      break;
    }
    used = next;
    fitting += 1;
  }
  return Math.max(fitting, 1);
};

interface Props {
  values: unknown[];
  onRemove: (index: number) => void;
}

const SelectedValueChips = ({ values, onRemove }: Props) => {
  const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);
  const [measureEl, setMeasureEl] = useState<HTMLElement | null>(null);
  const [availableWidth, setAvailableWidth] = useState(0);
  const [chipWidths, setChipWidths] = useState<number[]>([]);

  const readWidths = useCallback((container: HTMLElement | null, measureRow: HTMLElement | null) => {
    if (container === null || measureRow === null) {
      return;
    }
    setAvailableWidth(container.clientWidth);
    setChipWidths([...measureRow.children].map((chip) => (chip as HTMLElement).offsetWidth));
  }, []);

  // Runs during commit, so a newly added chip is measured before the browser paints the stale "+N".
  const handleMeasureRef = useCallback(
    (node: HTMLElement | null) => {
      setMeasureEl(node);
      readWidths(containerEl, node);
    },
    [containerEl, readWidths]
  );

  const measure = useCallback(() => readWidths(containerEl, measureEl), [containerEl, measureEl, readWidths]);
  useResizeObserver(containerEl, undefined, measure);

  const labels = values.map((value) => toLabel(toFilterValue(value)));
  const isMeasured = availableWidth > 0 && chipWidths.length > 0;
  const visibleCount = useMemo(() => {
    if (!isMeasured) {
      return labels.length;
    }
    const knownWidths = chipWidths.slice(0, labels.length);
    return Math.min(getVisibleCount(knownWidths, availableWidth, labels.length), labels.length);
  }, [availableWidth, chipWidths, isMeasured, labels.length]);
  const hiddenCount = labels.length - visibleCount;

  return (
    <Box
      ref={setContainerEl}
      sx={{ position: "relative", display: "flex", alignItems: "center", flex: 1, minWidth: 0, overflow: "hidden" }}
    >
      <Box
        key={labels.length}
        ref={handleMeasureRef}
        aria-hidden
        sx={{
          position: "absolute",
          visibility: "hidden",
          pointerEvents: "none",
          display: "flex",
          gap: `${CHIP_GAP}px`,
          width: "max-content",
        }}
      >
        {labels.map((label, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <Chip key={index} label={label} onDelete={noop} />
        ))}
      </Box>
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ alignItems: "center", flexWrap: "nowrap", minWidth: 0, ...(!isMeasured && { visibility: "hidden" }) }}
      >
        {labels.slice(0, visibleCount).map((label, index) => (
          <Chip
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={index}
            label={label}
            onDelete={(event: React.SyntheticEvent) => {
              event.stopPropagation();
              onRemove(index);
            }}
          />
        ))}
        {hiddenCount > 0 && <Typography variant="caption">+{hiddenCount}</Typography>}
      </Stack>
    </Box>
  );
};

export default SelectedValueChips;
